import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import AssignVariantDialog from "@dashboard/components/AssignVariantDialog";
import { DashboardCard } from "@dashboard/components/Card";
import { CardSpacer } from "@dashboard/components/CardSpacer";
import { ConfirmButtonTransitionState } from "@dashboard/components/ConfirmButton";
import FormSpacer from "@dashboard/components/FormSpacer";
import { DetailPageLayout } from "@dashboard/components/Layouts";
import { Savebar } from "@dashboard/components/Savebar";
import { WsmErrorFragment } from "@dashboard/graphql";
import useNavigator from "@dashboard/hooks/useNavigator";
import useProductSearch from "@dashboard/searches/useProductSearch";
import { mapEdgesToItems } from "@dashboard/utils/maps";
import { Box, Button, Input, Text } from "@saleor/macaw-ui-next";
import React, { useState } from "react";
import { useIntl } from "react-intl";

import {
  getWsmDealerErrorMessage,
  getWsmDealerFieldError,
  getWsmDealerFormErrors,
} from "../../errors";
import { groupMessages, railMessages } from "../../messages";
import { WsmDealerGroupFormData, WsmTierRowDraft } from "../../types";
import { wsmDealerCustomerListPath, wsmDealerGroupListPath } from "../../urls";
import { diffTierRows, makeDraftKey, mergePastedRows } from "../../utils";
import WsmDealerHelpCard from "../WsmDealerHelpCard/WsmDealerHelpCard";
import WsmDealerPasteDialog from "../WsmDealerPasteDialog/WsmDealerPasteDialog";
import WsmDealerTierPricesCard from "../WsmDealerTierPricesCard/WsmDealerTierPricesCard";

export interface WsmDealerGroupDetailsPageProps {
  title: string;
  initialData: WsmDealerGroupFormData;
  errors: WsmErrorFragment[];
  /** One error per tier row index, as the bulk payload named them. */
  tierRowErrors: Record<number, WsmErrorFragment>;
  disabled: boolean;
  saveButtonBarState: ConfirmButtonTransitionState;
  currencyCode: string | null;
  /** Null unless the group holds more prices than one screen loaded. */
  truncatedAt: number | null;
  /** Create has no group to hang prices on yet, so it hides the grid. */
  showTierPrices: boolean;
  /** Saved groups only: what the rail reports about this one. */
  groupId?: string;
  customerCount?: number;
  tierPriceCount?: number;
  onSubmit: (data: WsmDealerGroupFormData) => void;
  onDelete?: () => void;
}

export const WsmDealerGroupDetailsPage = ({
  title,
  initialData,
  errors,
  tierRowErrors,
  disabled,
  saveButtonBarState,
  currencyCode,
  truncatedAt,
  showTierPrices,
  groupId,
  customerCount,
  tierPriceCount,
  onSubmit,
  onDelete,
}: WsmDealerGroupDetailsPageProps) => {
  const intl = useIntl();
  const navigate = useNavigator();
  const [data, setData] = useState<WsmDealerGroupFormData>(initialData);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pasteOpen, setPasteOpen] = useState(false);
  const { search: searchProducts, result: searchProductsOpts } = useProductSearch({
    variables: { after: null, first: 20, query: "" },
  });
  const change = (patch: Partial<WsmDealerGroupFormData>) =>
    setData(current => ({ ...current, ...patch }));
  const codeError = getWsmDealerFieldError(errors, "code");
  const nameError = getWsmDealerFieldError(errors, "name");
  const formErrors = getWsmDealerFormErrors(errors, intl);
  const products = mapEdgesToItems(searchProductsOpts.data?.search) ?? [];
  // What one save would send, counted against the rows the server handed back.
  const pending = diffTierRows(initialData.rows, data.rows, "pending");
  const unsavedCount = pending.creates.length + pending.updates.length + pending.deleteIds.length;
  /** The break a freshly picked variant lands on, matching the paste default. */
  const PICKED_MIN_QUANTITY = 1;
  const handleVariantsAssign = (variants: Array<Record<string, any>>) => {
    // A variant already priced at this break keeps the row it has: the picker
    // re-lists everything it can see, so re-picking a priced variant used to add
    // a second row at the same break and earn DUPLICATE_TIER_BREAK on save.
    const onGrid = new Set(
      data.rows
        .filter(row => row.minQuantity === PICKED_MIN_QUANTITY)
        .map(row => row.variantId),
    );
    const added: WsmTierRowDraft[] = variants
      .filter(variant => !onGrid.has(variant.id))
      .map(variant => ({
        key: makeDraftKey("tier"),
        id: null,
        variantId: variant.id,
        sku: variant.sku ?? "",
        variantName: variant.name ?? "",
        productName: variant.product?.name ?? variant.name ?? "",
        minQuantity: PICKED_MIN_QUANTITY,
        amount: "",
      }));

    change({ rows: [...data.rows, ...added] });
    setPickerOpen(false);
  };

  return (
    <DetailPageLayout>
      <TopNav href={wsmDealerGroupListPath} title={title} />
      <DetailPageLayout.Content>
        <DashboardCard data-test-id="wsm-dealer-group-general">
          <DashboardCard.Header>
            <DashboardCard.Title>
              {intl.formatMessage(groupMessages.generalTitle)}
            </DashboardCard.Title>
          </DashboardCard.Header>
          <DashboardCard.Content>
            {formErrors.map(message => (
              <Text key={message} size={3} color="critical1" data-test-id="wsm-dealer-group-error">
                {message}
              </Text>
            ))}
            <Box display="grid" __gridTemplateColumns="1fr 1fr" gap={4}>
              <Input
                label={intl.formatMessage(groupMessages.fieldCode)}
                data-test-id="wsm-dealer-group-code"
                disabled={disabled}
                value={data.code}
                onChange={event => change({ code: event.target.value })}
                error={!!codeError}
                helperText={
                  getWsmDealerErrorMessage(codeError, intl) ??
                  intl.formatMessage(groupMessages.fieldCodeHelp)
                }
              />
              <Input
                label={intl.formatMessage(groupMessages.fieldName)}
                data-test-id="wsm-dealer-group-name"
                disabled={disabled}
                value={data.name}
                onChange={event => change({ name: event.target.value })}
                error={!!nameError}
                helperText={
                  getWsmDealerErrorMessage(nameError, intl) ??
                  intl.formatMessage(groupMessages.fieldNameHelp)
                }
              />
            </Box>
            <FormSpacer />
          </DashboardCard.Content>
        </DashboardCard>
        {showTierPrices && (
          <>
            <CardSpacer />
            <WsmDealerTierPricesCard
              rows={data.rows}
              rowErrors={tierRowErrors}
              currencyCode={currencyCode}
              disabled={disabled}
              truncatedAt={truncatedAt}
              unsavedCount={unsavedCount}
              onAdd={() => setPickerOpen(true)}
              onPaste={() => setPasteOpen(true)}
              onRemove={keys => change({ rows: data.rows.filter(row => !keys.includes(row.key)) })}
              onRowChange={(key, patch) =>
                change({
                  rows: data.rows.map(row => (row.key === key ? { ...row, ...patch } : row)),
                })
              }
            />
          </>
        )}
      </DetailPageLayout.Content>

      <DetailPageLayout.RightSidebar>
        {groupId ? (
          <DashboardCard data-test-id="wsm-dealer-group-rail">
            <DashboardCard.Header>
              <DashboardCard.Title>
                {intl.formatMessage(railMessages.groupTitle)}
              </DashboardCard.Title>
            </DashboardCard.Header>
            <DashboardCard.Content>
              <Box display="grid" gap={3}>
                <Box display="flex" justifyContent="space-between">
                  <Text size={3} color="default2">
                    {intl.formatMessage(railMessages.customers)}
                  </Text>
                  <Text size={3} data-test-id="wsm-dealer-group-rail-customers">
                    {customerCount ?? 0}
                  </Text>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Text size={3} color="default2">
                    {intl.formatMessage(railMessages.tierPrices)}
                  </Text>
                  <Text size={3} data-test-id="wsm-dealer-group-rail-tier-prices">
                    {tierPriceCount ?? 0}
                  </Text>
                </Box>
                <Button
                  variant="secondary"
                  onClick={() =>
                    navigate(`${wsmDealerCustomerListPath}?group=${encodeURIComponent(groupId)}`)
                  }
                  data-test-id="wsm-dealer-group-rail-customers-link"
                >
                  {intl.formatMessage(railMessages.viewCustomers)}
                </Button>
              </Box>
            </DashboardCard.Content>
          </DashboardCard>
        ) : (
          <WsmDealerHelpCard />
        )}
      </DetailPageLayout.RightSidebar>

      <AssignVariantDialog
        confirmButtonState="default"
        hasMore={searchProductsOpts.data?.search?.pageInfo?.hasNextPage ?? false}
        open={pickerOpen}
        onFetch={searchProducts}
        onFetchMore={() => undefined}
        loading={searchProductsOpts.loading}
        onClose={() => setPickerOpen(false)}
        onSubmit={handleVariantsAssign}
        products={products as never}
      />
      <WsmDealerPasteDialog
        open={pasteOpen}
        existingRows={data.rows}
        onClose={() => setPasteOpen(false)}
        onConfirm={(rows, replacedKeys) => {
          change({ rows: mergePastedRows(data.rows, rows, replacedKeys) });
          setPasteOpen(false);
        }}
      />

      <Savebar>
        {onDelete && <Savebar.DeleteButton onClick={onDelete} />}
        <Savebar.Spacer />
        <Savebar.CancelButton onClick={() => navigate(wsmDealerGroupListPath)} />
        <Savebar.ConfirmButton
          transitionState={saveButtonBarState}
          onClick={() => onSubmit(data)}
          disabled={disabled}
          data-test-id="wsm-dealer-group-save"
        />
      </Savebar>
    </DetailPageLayout>
  );
};

export default WsmDealerGroupDetailsPage;
